/**
 * /add-button[?code=XXXXXXXX] — the guided wizard that binds a button to an
 * account. Four steps, deliberately with Wi-Fi BEFORE the setup code: by the time
 * the code is entered/checked the device is already online, so
 * device_setup_status's `registered` reliably means "wrong code" rather than the
 * ambiguous "not online yet". (See docs/provisioning-and-security.md — this
 * ordering is why we don't need a provision-time code ledger.)
 *   'elder'   — "Who's this button for?": create the elder profile (new account)
 *               or pick the elder (>1 account). Skipped for a single existing elder.
 *   'wifi'    — get the button onto Wi-Fi (captive portal); wait for the amber light.
 *   'details' — name the room, and — ONLY if the code did not arrive in the URL —
 *               enter the setup code.
 *   'waiting' — poll device_setup_status until the button registers + connects,
 *               then auto-claim (setup_device 404s until the device is online).
 *
 * The sticker's QR deep-links here with ?code=, so QR users skip the code input
 * (their 'details' step is just the room) and never see the "re-check the code"
 * fallback — a scanned code is correct by construction. Back steps backwards
 * through the phases so a caregiver can always correct a wrong code in-flow.
 *
 * v1 simplification: caregiver picks the elder only if they're on more than one
 * account; device is auto-bound to the account's single voice profile (created
 * at signup). See docs/v1-ui-deferrals.md for the rationale.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useAccount } from '@/hooks/useAccount';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { AuthLayout } from '@/components/AuthLayout';
import { Spinner } from '@/components/ui/Spinner';
import { ElderProfileFields } from '@/components/ElderProfileFields';
import { HealthConsentCheckbox } from '@/components/HealthConsentCheckbox';
import { detectTimeZone } from '@/lib/timeZone';
import { setupDevice, deviceSetupStatus } from '@/lib/callables';
import { isSetupCodeShapeValid, canonicalizeSetupCode } from '@/lib/setupCode';
import { track } from '@/lib/analytics';
import { ensureUserProfileCallable } from '@/lib/auth';
import { useAccountSetupFallback } from '@/hooks/useAccountSetupFallback';
import { AccountSetupProblem } from '@/components/AccountSetupProblem';
import { AssistantWidget } from '@/components/AssistantWidget';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { saveElderProfileWithConsent } from '@/lib/firestore';
import { log } from '@/lib/logger';

type Phase = 'elder' | 'power' | 'wifi' | 'details' | 'waiting';
const PHASES: readonly Phase[] = ['elder', 'power', 'wifi', 'details', 'waiting'];

// Opening questions for the in-app assistant, tailored to the setup step so the
// suggestions fit where the caregiver actually is — e.g. no Wi-Fi troubleshooting
// on the naming step, where Wi-Fi is already done. Only the steps that open the
// panel bare (elder / details / waiting) ever show these; power/Wi-Fi auto-ask
// their light-gate question, so their conversation is never empty.
function setupStartersFor(phase: Phase, codeFromUrl: boolean): string[] {
  switch (phase) {
    case 'details':
      return codeFromUrl
        ? ['How does the button work?', 'What happens after I connect it?', "The app can't find my button"]
        : [
            'Where do I find the setup code?',
            "I can't read the code on the sticker",
            "The setup code isn't working",
          ];
    case 'waiting':
      return [
        "The app can't find my button",
        "The button's light isn't turning amber",
        'Where do I find the setup code?',
      ];
    case 'elder':
    default:
      return ['How does the button work?', 'What do I need to set it up?', 'Where do I find the setup code?'];
  }
}

/** The in-app help assistant, mounted on the setup screens. Controlled by the
 * page so the step "help me" buttons can open it with a matching pre-loaded
 * question; `context` (per step) sharpens the answers. Nothing until aid lands. */
function SetupHelp({
  aid,
  open,
  onOpenChange,
  context,
  starters,
  autoAsk,
  onAutoAsked,
  hideLauncher,
}: {
  aid: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  context: string;
  starters: string[];
  autoAsk: string | null;
  onAutoAsked: () => void;
  hideLauncher: boolean;
}) {
  if (!aid) return null;
  return (
    <AssistantWidget
      aid={aid}
      primaryCid={null}
      context={context}
      starters={starters}
      open={open}
      onOpenChange={onOpenChange}
      autoAsk={autoAsk}
      onAutoAsked={onAutoAsked}
      hideLauncher={hideLauncher}
    />
  );
}

export function SetupDevicePage() {
  const auth = useAuth();
  const [params, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlCode = (params.get('code') ?? '').trim();
  // Canonicalize the URL code the same way the backend hashes it (uppercase,
  // strip separators, fold O/I/L → 0/1/1) so what we display and send matches
  // the device's real code.
  const initialCode = canonicalizeSetupCode(urlCode);
  // Did the code arrive in the URL (QR deep-link)? Drives two things: the
  // 'details' step only asks for a code when it DIDN'T, and the failure UX only
  // offers a "re-check the code" path for a typed code (a scanned one is correct
  // by construction — a stuck setup there is a Wi-Fi problem, not the code).
  const codeFromUrl = isSetupCodeShapeValid(initialCode);

  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const { memberships, loading: membershipsLoading } = useMyMemberships(uid);

  const [code, setCode] = useState(initialCode);
  // The step lives in the URL (?step=), NOT in component state. Two reasons, both
  // learned from a real setup: (1) the browser/OS Back button is the natural way to
  // ask for the PREVIOUS STEP, and with the step in component state it instead
  // unmounted the wizard and dropped the caregiver on the home screen with all
  // progress lost; (2) the Wi-Fi step sends them out of the app to the device's
  // captive portal, so coming back via Back is the common case, not an edge case.
  // Same-route search-param changes don't unmount, so `code`/`room` survive
  // stepping back and forward — only leaving /add-button entirely resets them.
  const rawStep = params.get('step');
  const phase: Phase = PHASES.includes(rawStep as Phase) ? (rawStep as Phase) : 'elder';
  // Forward moves PUSH (so Back returns here); corrections REPLACE (so Back doesn't
  // bounce the caregiver into the state they just corrected). Backward moves are
  // navigate(-1), never a push — that keeps the in-app "Back" button and the
  // browser's Back button the same single operation.
  const setPhase = useCallback(
    (next: Phase, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('step', next);
          return p;
        },
        { replace: opts?.replace ?? false },
      );
    },
    [setSearchParams],
  );
  // Guards the auto-claim so setupDevice fires at most once even if the poll
  // effect re-runs (StrictMode / a dep change). Reset when (re-)entering 'waiting'.
  const claimingRef = useRef(false);
  const [accountId, setAccountId] = useState('');
  const [room, setRoom] = useState('');
  const [elderName, setElderName] = useState('');
  const [timeZone, setTimeZone] = useState(detectTimeZone);
  const [healthConsent, setHealthConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'waiting' has polled long enough (SLOW_MS) that we surface a help panel — but
  // polling continues underneath, so a device that heartbeats a beat later still
  // auto-completes and the panel just vanishes. So it's guidance, not a give-up.
  const [slow, setSlow] = useState(false);
  // Last poll's status, so the "still looking" panel can tell a WRONG CODE
  // (never registered) apart from a FOUND-BUT-OFFLINE button (registered, not yet
  // connected). Conflating the two made a valid code read as a bad code.
  const [lastStatus, setLastStatus] = useState<{ registered: boolean; connected: boolean } | null>(null);
  // In-app assistant. It's rendered inside each step (via the shared `setupHelp`
  // element) and reset on every step change, so it never lingers open/empty. Each
  // step's inline "I need help" button opens it (power/Wi-Fi with a matching
  // pre-loaded question, the form steps bare); `helpContext` sharpens answers.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpAutoAsk, setHelpAutoAsk] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId && memberships.length > 0) {
      setAccountId(memberships[0]?.id ?? '');
    }
  }, [memberships, accountId]);

  const { account, loading: accountLoading } = useAccount(accountId || null);
  // First-time setup combines the device claim with naming the elder if the
  // caregiver hasn't yet — saves bouncing them to a separate page.
  const needsElderProfile = !!account && !account.elderName.trim();
  const showAccountPicker = memberships.length > 1;
  // The 'elder' step is only worth showing when there's something to ask: a new
  // elder to name, or a choice between several. A single existing elder skips it.
  const needsElderStepNow = needsElderProfile || showAccountPicker;
  // Latch it: the details step SAVES elderName, which flips needsElderStepNow
  // false mid-flow. Without the latch, backing onto the pre-skip entry re-fires
  // the elder->power auto-skip and Back appears to do nothing — the same felt
  // failure this change exists to remove.
  const everNeededElder = useRef(false);
  if (needsElderStepNow) everNeededElder.current = true;
  const needsElderStep = needsElderStepNow || everNeededElder.current;
  // The first step the caregiver actually sees. Its "Back" leaves the wizard
  // rather than moving a step, so it says so — the same word meaning "previous
  // step" everywhere else and "abandon setup" here is how progress got lost.
  const isFirstStep = phase === (needsElderStep ? 'elder' : 'power');

  // Seed the elder form when the selected account first arrives, and re-seed on a
  // picker switch — but never on a repeat snapshot of the SAME account (useAccount
  // re-emits a fresh object per frame, which would clobber typing). Keying on the
  // id gives us both: a switch changes it, a re-emission does not.
  const seededAccountId = useRef<string | null>(null);
  useEffect(() => {
    if (account && seededAccountId.current !== account.id) {
      seededAccountId.current = account.id;
      setElderName(account.elderName ?? '');
      setTimeZone(account.timeZone || detectTimeZone());
      // Consent is person-specific ("someone who has agreed") — a tick for
      // one person must not survive switching the picker to another.
      setHealthConsent(false);
    }
  }, [account]);

  // Every step needs the account (elder naming, the claim's accountId). Until the
  // post-signup scaffolding lands, neither memberships nor the account doc
  // populate — show a spinner. Snapshot listeners pick up the new docs as they
  // appear, so the page self-recovers without a refresh.
  const provisioning =
    auth.state === 'signedIn' &&
    (membershipsLoading ||
      memberships.length === 0 ||
      (!!accountId && (accountLoading || !account)));

  // Escape hatch if scaffolding never completes (e.g. App Check failure at
  // signup) — surfaces an error + retry instead of an infinite spinner.
  const noMembership = auth.state === 'signedIn' && memberships.length === 0;
  const setup = useAccountSetupFallback(noMembership);

  // Belt-and-braces: if a freshly-signed-up caregiver waits a few seconds with no
  // memberships visible, re-run the (idempotent) provisioning callable. Covers an
  // orphaned signup callable or a lagging index listener. One attempt per mount.
  const provisioningAttempted = useRef(false);
  useEffect(() => {
    if (
      auth.state !== 'signedIn' ||
      membershipsLoading ||
      memberships.length > 0 ||
      provisioningAttempted.current
    ) {
      return;
    }
    const t = setTimeout(() => {
      if (provisioningAttempted.current) return;
      provisioningAttempted.current = true;
      log.info('setup-device: no memberships after wait — running ensure_user_profile recovery');
      ensureUserProfileCallable({
        displayName: auth.state === 'signedIn' ? auth.user.displayName ?? undefined : undefined,
        locale: navigator.language || 'en-GB',
      }).catch((e) => {
        log.error('setup-device: recovery callable failed', { error: String(e) });
        provisioningAttempted.current = false;
      });
    }, 3_000);
    return () => clearTimeout(t);
  }, [auth, membershipsLoading, memberships.length]);

  // Auto-skip the 'elder' step once the account has loaded and there's nothing to
  // ask (single existing elder) — QR/single-account users go straight to power-on.
  useEffect(() => {
    if (phase === 'elder' && !provisioning && account && !needsElderStep) {
      // REPLACE, never push: 'elder' isn't a real step for this caregiver, so a
      // pushed entry would make Back bounce elder→power→elder forever.
      setPhase('power', { replace: true });
    }
  }, [phase, provisioning, account, needsElderStep, setPhase]);

  // Reset the once-only claim guard when LEAVING 'waiting', so any re-entry —
  // including the browser Forward button, which the URL-driven step newly makes
  // possible — starts clean. Resetting on entry instead would be defeated by
  // StrictMode's double-invoke. Without this, Back-during-claim then Forward
  // leaves the poll returning early forever: a permanent spinner on a device
  // that did in fact get claimed. (Re-claiming is safe: setup_device is
  // idempotent for a same-account re-setup.)
  useEffect(() => {
    if (phase !== 'waiting') claimingRef.current = false;
  }, [phase]);

  // A hand-edited or stale URL can name a step that can't run: 'waiting' polls on
  // the setup code, so without one it would spin forever. Send those back to the
  // step that can collect it. REPLACE, so Back doesn't return to the dead state.
  useEffect(() => {
    if (phase === 'waiting' && !isSetupCodeShapeValid(code)) {
      setPhase('details', { replace: true });
    }
  }, [phase, code, setPhase]);

  // While 'waiting': poll the cheap read until the button is registered (TOFU) AND
  // connected, THEN auto-claim once. Polling the read avoids hammering the
  // rate-limited, side-effecting setup_device. In this reordered flow the device
  // is already online, so it usually clears on the first tick; if not, after
  // SLOW_MS we surface a help panel but KEEP polling (so a slightly-late heartbeat
  // still auto-completes).
  useEffect(() => {
    if (phase !== 'waiting' || !code || !accountId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    const SLOW_MS = 10_000;
    const POLL_MS = 2_500;
    setSlow(false);
    setLastStatus(null);
    async function tick() {
      if (cancelled) return;
      let ready = false;
      try {
        const res = await deviceSetupStatus({ setupCode: code });
        // Claim only when registered AND currently connected: a re-claimed
        // (unlinked) button stays registered while offline in captive portal.
        ready = !!res.data.registered && res.data.connected;
        setLastStatus({ registered: !!res.data.registered, connected: !!res.data.connected });
      } catch (e) {
        // Transient (network blip / brief rate-limit): retry on the next tick.
        log.warn('setup-device: status poll failed, will retry', { error: String(e) });
      }
      if (cancelled) return;
      if (ready) {
        if (claimingRef.current) return; // never claim twice
        claimingRef.current = true;
        try {
          await setupDevice({ setupCode: code, accountId, roomLabel: room });
          track('device_claimed');
          if (cancelled) return;
          navigate(`/accounts/${accountId}/voice`, {
            replace: true,
            state: { justClaimed: true, room: room.trim() },
          });
        } catch (e) {
          if (cancelled) return;
          claimingRef.current = false;
          setError(e instanceof Error ? e.message.replace(/^FirebaseError:\s*/, '') : 'Setup failed.');
          setPhase('details', { replace: true });
        }
        return;
      }
      if (Date.now() - startedAt > SLOW_MS) setSlow(true);
      timer = setTimeout(tick, POLL_MS);
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase, code, accountId, room, navigate, setPhase]);

  const elderHeading = useMemo(
    () => memberships.find((m) => m.id === accountId)?.accountName?.trim() || 'this person',
    [memberships, accountId],
  );

  // Reset help whenever the step changes so the assistant never lingers open (and
  // re-mounts empty) on the next step — it's opened fresh per step.
  useEffect(() => {
    setHelpOpen(false);
    setHelpAutoAsk(null);
  }, [phase]);

  // Per-step context sharpens the assistant's answers; the step "help me" buttons
  // open it with a matching pre-loaded question via openHelp.
  // The tag also carries HOW the code got here (scanned vs typed), because the
  // right triage for "the app can't find your button" differs completely: a
  // scanned code is correct by construction, so sending them back to re-check it
  // wastes their time; a typed one is the most likely cause and must be ruled
  // out before anything destructive. Server-side notes only — the client sends a
  // tag, never prompt text (see _ASSISTANT_CONTEXT_NOTES).
  const helpContext =
    phase === 'power'
      ? 'device-setup-power'
      : phase === 'wifi'
        ? 'device-setup-wifi'
        : phase === 'details'
          ? codeFromUrl
            ? 'device-setup-code-qr'
            : 'device-setup-code'
          : phase === 'waiting'
            ? codeFromUrl
              ? 'device-setup-waiting-qr'
              : 'device-setup-waiting-typed'
            : 'device-setup';
  // Optional pre-loaded question: the power/Wi-Fi steps pass their light-gate
  // question; the form steps open the panel bare (starters only).
  const openHelp = (question?: string) => {
    setHelpAutoAsk(question ?? null);
    setHelpOpen(true);
  };
  const helpStarters = setupStartersFor(phase, codeFromUrl);
  const setupHelp = (
    <SetupHelp
      // Remount per step so the conversation starts fresh — otherwise the prior
      // step's Q&A lingers in the panel and bleeds into the next step's history.
      key={phase}
      aid={accountId}
      open={helpOpen}
      onOpenChange={setHelpOpen}
      context={helpContext}
      starters={helpStarters}
      autoAsk={helpAutoAsk}
      onAutoAsked={() => setHelpAutoAsk(null)}
      // Every wizard step has its own inline "I need help" button, so the floating
      // corner launcher is always hidden here — help stays at the same level as the
      // step's primary action, consistently across the flow.
      hideLauncher
    />
  );

  if (auth.state === 'loading') {
    return (
      <AuthLayout title="Set up this button">
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (auth.state === 'signedOut') {
    const next = encodeURIComponent(urlCode ? `/add-button?code=${urlCode}` : '/add-button');
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Account still scaffolding (or failed) — every step below needs the account.
  if (provisioning) {
    return (
      <AuthLayout title="Setting up your account…">
        {setup.failed ? (
          <AccountSetupProblem onRetry={setup.retry} retrying={setup.retrying} />
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Spinner />
            <p className="text-sm text-ink-soft">Just a moment…</p>
          </div>
        )}
      </AuthLayout>
    );
  }

  // Step 1 — Who's this button for? (create/pick the elder). Only reached when
  // there's something to ask; otherwise the effect above has advanced to 'wifi'.
  if (phase === 'elder') {
    if (!needsElderStep) {
      // Account loaded, nothing to ask — the effect is advancing to 'power'; show a
      // spinner rather than flashing an empty form for a frame.
      return (
        <AuthLayout title="Set up this button">
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        </AuthLayout>
      );
    }
    return (
      <AuthLayout title="Who's this button for?">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (needsElderProfile && !elderName.trim()) {
              setError('Please enter the first name of the person you care for.');
              return;
            }
            setError(null);
            // NB: the elder profile is NOT saved here — it's saved on the details
            // step (just before we poll). Deferring it keeps the account
            // un-mutated through elder→power→wifi→details, so `needsElderStep`
            // stays stable and Back navigation returns here (rather than kicking a
            // new-account caregiver out of the wizard once the name would save).
            setPhase('power');
          }}
        >
          {showAccountPicker && (
            <Field label="This button is for" htmlFor="aid">
              <select
                id="aid"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                {memberships.map((m) => (
                  <option key={m.id} value={m.id}>
                    {/* An account with no elder name yet is the "set up someone
                        new" slot — naming it below is what fills it in. */}
                    {m.accountName || 'Someone new'}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {needsElderProfile && (
            <>
              <ElderProfileFields
                elderName={elderName}
                onElderNameChange={setElderName}
                timeZone={timeZone}
                onTimeZoneChange={setTimeZone}
              />
              <HealthConsentCheckbox
                checked={healthConsent}
                onCheckedChange={setHealthConsent}
                idPrefix="setup"
              />
            </>
          )}
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={!accountId || !account || (needsElderProfile && !healthConsent)}
            >
              Next
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() => openHelp()}
            >
              I need help
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 self-center"
              onClick={() => {
                setError(null);
                navigate(-1);
              }}
            >
              {isFirstStep ? 'Cancel setup' : 'Back'}
            </Button>
          </div>
        </form>
        {setupHelp}
      </AuthLayout>
    );
  }

  // Step 2 — power on. Plug in and wait for the slow blue pulse (ready to set up).
  // The primary full-width button IS the "yes, it's flashing blue" confirmation;
  // the quieter one opens the assistant pre-loaded with the matching question.
  if (phase === 'power') {
    return (
      <AuthLayout title="Plug in the button">
        <p className="mb-4 text-sm text-ink-soft">
          Plug the button into power with the USB-C cable.
        </p>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-soft">
          <li>Connect the USB-C cable and switch it on at the wall.</li>
          <li>
            Give it a few seconds — the light ring settles into a{' '}
            <span className="font-medium">slow blue pulse</span>. That means it&apos;s
            ready to set up.
          </li>
        </ol>
        <div className="mt-6 flex flex-col gap-2">
          <Button
            type="button"
            size="lg"
            // Tinted to match the light it's confirming (blue isn't in our warm
            // palette, so set it explicitly); the Wi-Fi step's amber confirm keeps
            // the brand accent, which already reads as the warm-amber light.
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => {
              setError(null);
              setPhase('wifi');
            }}
          >
            The button is flashing blue
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => openHelp("My button isn't flashing blue")}
          >
            I need help
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 self-center"
            onClick={() => {
              setError(null);
              navigate(-1);
            }}
          >
            {isFirstStep ? 'Cancel setup' : 'Back'}
          </Button>
        </div>
        {setupHelp}
      </AuthLayout>
    );
  }

  // Step 3 — Wi-Fi. Join the setup network + enter home Wi-Fi. The primary
  // full-width button IS the "yes, the light is amber" confirmation. Comes BEFORE
  // the code so the device is online by the time the code is entered/checked.
  if (phase === 'wifi') {
    return (
      <AuthLayout title="Connect the button to Wi-Fi">
        <p className="mb-4 text-sm text-ink-soft">Now get the button onto your home Wi-Fi.</p>
        <WifiSteps />
        <div className="mt-6 flex flex-col gap-2">
          <Button
            type="button"
            size="lg"
            className="w-full"
            onClick={() => {
              setError(null);
              setPhase('details');
            }}
          >
            The light is a steady amber
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => openHelp("The button's light isn't turning amber")}
          >
            I need help
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 self-center"
            onClick={() => {
              setError(null);
              navigate(-1);
            }}
          >
            Back
          </Button>
        </div>
        {setupHelp}
      </AuthLayout>
    );
  }

  // Step 3 — name the room, and enter the code IF it didn't arrive from the QR.
  // Submitting starts the poll; the code lands here (after Wi-Fi), so a wrong one
  // is caught right away.
  if (phase === 'details') {
    return (
      <AuthLayout title="Name the button">
        <p className="mb-4 text-sm text-ink-soft">
          Setting up {elderHeading === 'this person' ? 'the button' : `${elderHeading}'s button`}.
        </p>
        <form
          className="flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!codeFromUrl && !isSetupCodeShapeValid(code)) {
              setError('A setup code is 8 letters and numbers — check what you typed.');
              return;
            }
            setError(null);
            // Persist the elder profile now (deferred from the elder step) so a
            // save failure surfaces before we start polling. Idempotent: on a
            // wrong-code retry back to this step, needsElderProfile is already
            // false, so we skip it.
            if (needsElderProfile) {
              // Never stamp a consent record that wasn't ticked: if the
              // elder became unnamed AFTER the elder step (so the checkbox
              // was never shown), send the caregiver back rather than
              // fabricating an Art 9 grant.
              if (!healthConsent) {
                setError('Please go back and confirm the consent box before continuing.');
                return;
              }
              try {
                if (!uid) throw new Error('Not signed in.');
                await saveElderProfileWithConsent(
                  accountId,
                  { elderName: elderName.trim(), timeZone },
                  uid,
                );
              } catch (e) {
                setError(e instanceof Error ? e.message.replace(/^FirebaseError:\s*/, '') : 'Could not save the profile.');
                return;
              }
            }
            if (!codeFromUrl) setCode(canonicalizeSetupCode(code));
            claimingRef.current = false;
            setPhase('waiting');
          }}
        >
          {!codeFromUrl && (
            <Field
              label="Setup code"
              htmlFor="setup-code"
              hint="The 8-character code on the sticker on your button."
            >
              <Input
                id="setup-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  if (error) setError(null);
                }}
                placeholder="e.g. K4PQ7D2M"
                autoFocus
              />
            </Field>
          )}
          <Field label="Room (optional)" htmlFor="room">
            <Input
              id="room"
              placeholder="Bedside"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              maxLength={100}
            />
          </Field>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={!codeFromUrl && !code.trim()}
            >
              Connect button
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() => openHelp()}
            >
              I need help
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 self-center"
              onClick={() => {
                setError(null);
                navigate(-1);
              }}
            >
              Back
            </Button>
          </div>
        </form>
        {setupHelp}
      </AuthLayout>
    );
  }

  // Step 4 — waiting for the button to come online, then auto-claiming. After
  // SLOW_MS a help panel appears while polling continues underneath.
  return (
    <AuthLayout title="Setting up the button…">
      <div className="flex flex-col items-center gap-3 py-2 text-center">
        <Spinner />
        <p className="text-sm font-medium text-ink">Looking for your button…</p>
        {!slow && (
          <p className="text-sm text-ink-soft">
            Once it&apos;s on Wi-Fi (its light is a steady amber), we&apos;ll link it
            automatically.
          </p>
        )}
        {slow && (
          <div className="mt-1 w-full rounded-lg border border-paper-border bg-paper-alt px-4 py-3 text-left text-sm">
            {lastStatus && !lastStatus.registered ? (
              <>
                <p className="font-medium text-ink">We can&apos;t find that button yet</p>
                <p className="mt-1 text-ink-soft">
                  Double-check the <span className="font-medium">setup code</span> on the
                  button&apos;s sticker, and that the button is plugged in with its light on.
                  (A brand-new button also needs a minute on Wi-Fi before it shows up.)
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-ink">
                  {lastStatus?.registered
                    ? 'Found it — waiting for it to come online'
                    : 'Still looking…'}
                </p>
                <p className="mt-1 text-ink-soft">
                  Look at the light ring on the button — is it a{' '}
                  <span className="font-medium">steady amber</span>? That means it&apos;s on
                  Wi-Fi, and we&apos;ll pick it up in a moment.
                </p>
              </>
            )}
            <div className="mt-2 flex flex-col gap-1">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setError(null);
                  setPhase('wifi', { replace: true });
                }}
              >
                The light isn&apos;t amber — redo Wi-Fi
              </Button>
              {!codeFromUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setError(null);
                    setPhase('details', { replace: true });
                  }}
                >
                  Double-check the setup code
                </Button>
              )}
            </div>
          </div>
        )}
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={() => openHelp()}
        >
          I need help
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 self-center"
          onClick={() => {
            setError(null);
            navigate(-1);
          }}
        >
          Cancel
        </Button>
      </div>
      {setupHelp}
    </AuthLayout>
  );
}

// The numbered steps stay terse — four actions, nothing to read past. Everything
// that only matters when the step goes wrong lives in the disclosure below, and
// it lives THERE rather than behind "I need help" for one reason: at this step
// the phone is joined to the button's setup network, which has no internet by
// design, so the assistant is unreachable exactly here. Help that only exists
// behind the assistant would not have reached the council worker whose stuck
// setup on 2026-07-28 prompted all of this.
function WifiSteps() {
  return (
    <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-soft">
      <li>
        On your phone or laptop, open Wi-Fi settings and join the network{' '}
        <span className="font-mono">RemindMeVoice-Setup</span>.
        <StepHelp summary="Your phone says there's no internet?">
          <p>
            Expected — the button&apos;s setup network doesn&apos;t provide any. If your phone
            offers, choose to stay connected to it.
          </p>
        </StepHelp>
      </li>
      <li>
        A setup page should open automatically — you may need to tap{' '}
        <span className="font-medium">“Sign in to this network”</span> for it to appear.
        <StepHelp summary="Not sure it's the right page, or it never opened?">
          {/* Affirmation first, description second: the caregiver this was
              written for didn't need the page described, she needed telling she
              hadn't gone wrong — she had stopped and phoned. */}
          <p>
            <span className="font-medium text-ink">That&apos;s the right page.</span> It&apos;s the
            button&apos;s own, headed <span className="font-medium">“MAC Address”</span> and{' '}
            <span className="font-medium">“WiFi Networks”</span>, so it doesn&apos;t mention
            RemindMeVoice and never asks you to log in. Ignore the{' '}
            <span className="font-medium">“OTA Update”</span> box at the bottom.
          </p>
          <p>If no page appeared at all:</p>
          <ul className="list-disc space-y-1 pl-5">
            {/* The link text is the full URL on purpose: anyone who types it
                rather than tapping copies the http:// prefix from what's on
                screen, which is what stops the phone searching for it. */}
            <li>
              Open{' '}
              <ExternalLink href="http://192.168.4.1" className="font-mono text-accent-700 underline">
                http://192.168.4.1
              </ExternalLink>
              .
            </li>
            <li>Turn mobile data off for a couple of minutes, then try again.</li>
            <li>
              Still nothing? In Wi-Fi settings, forget{' '}
              <span className="font-mono">RemindMeVoice-Setup</span> and join it again.
            </li>
          </ul>
        </StepHelp>
      </li>
      {/* The password clause stays ON the step rather than in its fold: someone
          hesitating over which password to type has no reason to open one, and
          the wrong guess means typing their RemindMeVoice password into a
          device's web page. */}
      <li>
        Pick your home Wi-Fi from the list and enter its password — your{' '}
        <span className="font-medium">home Wi-Fi</span> password, not your RemindMeVoice one.
        <StepHelp summary="Your network isn't listed?">
          <p>
            Reload the page — the button may still be scanning. If it&apos;s still missing, choose
            the <span className="font-medium">2.4 GHz</span> version of your network (often ending
            “-2G”); the button can&apos;t use 5 GHz.
          </p>
        </StepHelp>
      </li>
      {/* This step needs a fold more than the others, not less. If the details
          didn't take, the button never joined — so it never dropped its setup
          network, so the phone is probably still on it and the "I need help"
          assistant below is unreachable. The one answer worth having has to be
          on the page. */}
      <li>
        Give it up to a minute to join your network — its light then turns a{' '}
        <span className="font-medium">steady amber</span>.
        <StepHelp summary="Still not amber?">
          <p>
            Still pulsing blue after a minute or two? The details didn&apos;t take. Rejoin{' '}
            <span className="font-mono">RemindMeVoice-Setup</span> and enter them again, checking
            the password carefully — a wrong password looks exactly like this, with no error.
            There&apos;s no need to reset or unplug the button.
          </p>
          {/* Purple is the pilot-unit failure: joined fine, reached nothing,
              because the network intercepts traffic pending a browser login.
              The button rescues itself in ~70–90s (docs/device-recovery.md),
              so the instruction is to wait rather than to intervene. */}
          <p>
            Gone <span className="font-medium">purple</span> instead? It joined that network but
            can&apos;t reach us — usually a public or shared network that wants you to sign in
            through a browser first, which the button can&apos;t do. It gives up on its own after a
            minute or so and reopens its setup page, so wait for the blue pulse to return and then
            choose the home Wi-Fi rather than a public one.
          </p>
        </StepHelp>
      </li>
    </ol>
  );
}

/** One step's worth of "what if this didn't work", folded next to that step so
 * nobody reads another step's troubleshooting to reach their own. A plain
 * `<details>`: static, so it still works on the button's setup network — which
 * has no internet by design, putting the assistant out of reach exactly here.
 * That is the whole reason this help is inline rather than behind "I need
 * help". The summary must name the SYMPTOM, not offer help in the abstract:
 * it is the only text a stuck caregiver sees before deciding to open it. */
function StepHelp({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="mt-1.5">
      <summary className="cursor-pointer rounded underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500">
        {summary}
      </summary>
      <div className="mt-1.5 space-y-1.5">{children}</div>
    </details>
  );
}
